'use client';

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useRef,
  useEffect
} from 'react';
import {
  INTERVIEW_DURATION,
  INTRODUCTION_QUESTION,
  OUTRO_QUESTION,
  STATE,
  WRAP_UP_QUESTIONS
} from '@/components/interview/shared/InterviewConstants';
import { Topic, Answer, Message } from '@/lib/types/interview';
import {
  requestMicrophonePermission,
  getInterviewQuestions,
  getInterviewStatus
} from '@/components/interview/shared/InterviewService';
import { captureEvent } from '@/lib/utils/posthog';
import { POSTHOG_EVENTS } from '@/lib/utils/posthog';
import { InterviewStatus } from '@/components/interview/shared/InterviewTypes';
import { useRouter, useParams } from 'next/navigation';
import { toast } from 'sonner';

import {
  checkAuth,
  getCandidateId
} from '@/components/interview/shared/InterviewService';
import { SessionData } from '@/components/interview/shared/InterviewTypes';
import { VideoRecorderHandle } from '@/components/ui/video-recorder';
import { generateFollowUpQuestion } from '@/lib/utils/interview';

import { createClient } from '@/lib/supabase/client';
import { useMediaStream } from '@/hooks/useMediaStream';
import { InterviewData } from '@/lib/utils/code-review';
import { Interview } from '@/lib/types/candidate';

interface Question {
  id: string;
  text: string;
}

interface InterviewContextType {
  // State
  currentState: string;
  timer: number;
  conversation: Topic[];
  setConversation: React.Dispatch<React.SetStateAction<Topic[]>>;
  currentTopicIdx: number;
  setCurrentTopicIdx: React.Dispatch<React.SetStateAction<number>>;
  answeredQuestions: Answer[];
  setAnsweredQuestions: React.Dispatch<React.SetStateAction<Answer[]>>;
  isFollowUp: boolean;
  currentFollowUpIdx: number;
  pendingNextStep: boolean;
  interviewStarted: boolean;
  isLoading: boolean;
  consentGiven: boolean;
  deviceSetupShown: boolean;
  consentVisible: boolean;
  streamRef: React.RefObject<MediaStream | null>;
  screeningInfo: string | null;
  h1AnimationComplete: boolean;
  emojiAnimationComplete: boolean;
  showConsentModal: boolean;
  loadingAuth: boolean;
  sessionData: SessionData | null;
  conversationId: string | null;
  candidateId: string | null;
  shouldStartRecording: boolean;
  showRecorder: boolean;
  timeWarningState: 'none' | 'almost_done' | 'done';
  setTimeWarningState: React.Dispatch<
    React.SetStateAction<'none' | 'almost_done' | 'done'>
  >;
  mediaStream: MediaStream | null;
  currentInterview: Interview;
  permissionsRevoked: boolean;

  // Actions
  setConsentGiven: (value: boolean) => void;
  resetInterview: () => void;
  interviewStatus: InterviewStatus | null;
  setInterviewStatus: React.Dispatch<
    React.SetStateAction<InterviewStatus | null>
  >;
  setDeviceSetupShown: (value: boolean) => void;
  setConsentVisible: (value: boolean) => void;
  handleDeviceSetupComplete: () => void;
  handleInterviewStart: () => void;
  setH1AnimationComplete: (value: boolean) => void;
  setEmojiAnimationComplete: (value: boolean) => void;
  setShowConsentModal: (value: boolean) => void;
  setConversationId: (value: string | null) => void;
  setShouldStartRecording: (value: boolean) => void;
  setShowRecorder: (value: boolean) => void;
  initializeMediaStream: () => Promise<MediaStream>;
  stopMediaStream: () => void;
  setInterviewStarted: (value: boolean) => void;
  setCurrentState: (value: string) => void;
  currentAgentMessage: string | null;
  setCurrentAgentMessage: (value: string | null) => void;
  setCurrentInterview: (value: Interview | null) => void;

  // Refs
  videoRecorderRef: React.RefObject<VideoRecorderHandle>;
  audioRef: React.RefObject<HTMLAudioElement>;
}

const InterviewContext = createContext<InterviewContextType | undefined>(
  undefined
);

export function InterviewProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const params = useParams();
  // Real state
  const [conversation, setConversation] = useState<Topic[]>([]);
  const [currentTopicIdx, setCurrentTopicIdx] = useState(0);

  // State
  const [interviewStatus, setInterviewStatus] =
    useState<InterviewStatus | null>(null);
  const [currentState, setCurrentState] = useState<string>(
    STATE.START_INTERVIEW
  );
  const [timer, setTimer] = useState(0);

  const [answeredQuestions, setAnsweredQuestions] = useState<Answer[]>([]);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [currentFollowUpIdx, setCurrentFollowUpIdx] = useState(0);
  const [pendingNextStep, setPendingNextStep] = useState(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [consentGiven, setConsentGiven] = useState(false);
  const [deviceSetupShown, setDeviceSetupShown] = useState(false);
  const [consentVisible, setConsentVisible] = useState(true);
  const [interviewTakenId, setInterviewTakenId] = useState<string | null>(null);
  const [currentAgentMessage, setCurrentAgentMessage] = useState<string | null>(
    null
  );

  // Reset device setup when interview taken ID changes
  useEffect(() => {
    setDeviceSetupShown(false);
    setConsentVisible(true);
    setInterviewStarted(false);
    setConsentGiven(false);

    // Clear any persisted state for this interview
    if (interviewTakenId) {
      localStorage.removeItem(`webcam-setup-${interviewTakenId}`);
      localStorage.removeItem(`interview-started-${interviewTakenId}`);
      localStorage.removeItem(`wrap-up-started-${interviewTakenId}`);
    }
  }, [interviewTakenId]);

  useEffect(() => {
    const fetchCurrentInterview = async () => {
      if (interviewTakenId) {
        // First, fetch the interviews_taken record to get the interview_id
        const { data: interviewTaken, error: interviewTakenError } =
          await supabase
            .from('interviews_taken')
            .select('*')
            .eq('id', interviewTakenId)
            .single();

        if (interviewTakenError) {
          console.error(
            'Error fetching interview taken record:',
            interviewTakenError
          );
          return;
        }

        // Set the interview status immediately if it's finished
        if (interviewTaken?.status === 'finished') {
          setInterviewStatus('finished');
        } else if (interviewTaken?.status) {
          setInterviewStatus(interviewTaken.status as InterviewStatus);
        }

        if (interviewTaken?.interview_id) {
          // Then, fetch the actual interview using the interview_id
          const { data: currentInterview, error: interviewError } =
            await supabase
              .from('interviews')
              .select('*')
              .eq('id', interviewTaken.interview_id)
              .single();

          if (interviewError) {
            console.error('Error fetching current interview:', interviewError);
          } else {
            setCurrentInterview(currentInterview as Interview);
          }
        } else {
          console.error('No interview_id found in interviews_taken record');
        }
      }
    };

    fetchCurrentInterview();
  }, [interviewTakenId]);

  // New states from technical-interview.tsx
  const [screeningInfo, setScreeningInfo] = useState<string | null>(null);
  const [h1AnimationComplete, setH1AnimationComplete] = useState(false);
  const [emojiAnimationComplete, setEmojiAnimationComplete] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const [shouldStartRecording, setShouldStartRecording] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [currentInterview, setCurrentInterview] = useState<Interview | null>(
    null
  );
  const [timeWarningState, setTimeWarningState] = useState<
    'none' | 'almost_done' | 'done'
  >('none');

  // Refs
  const videoRecorderRef = useRef<VideoRecorderHandle>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const prevStateRef = useRef(currentState);
  const recordingCutoffTriggeredRef = useRef(false);

  const supabase = createClient();

  // Media stream management
  const {
    stream,
    streamRef,
    initializeStream,
    stopStream,
    permissionsRevoked
  } = useMediaStream();

  // Set interview taken ID from URL params
  useEffect(() => {
    if (params?.interview_taken) {
      setInterviewTakenId(params.interview_taken as string);
    }
  }, [params]);

  // Load screening info
  useEffect(() => {
    const storedInfo = localStorage.getItem('screening_information');
    if (storedInfo) {
      setScreeningInfo(storedInfo);
    }
  }, []);

  // Auth initialization
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we're on a page that requires authentication
        const currentPath = window.location.pathname;
        const requiresAuth = currentPath.includes('/candidate/interviews/');

        if (!requiresAuth) {
          // Skip auth check for public pages
          setLoadingAuth(false);
          setIsLoading(false);
          return;
        }

        const session = await checkAuth();
        setSessionData(session as unknown as SessionData);

        // Only get candidate ID if we're in an interview context
        if (interviewTakenId) {
          try {
            const candidateId = await getCandidateId(session.user.id);
            setCandidateId(candidateId);
          } catch (error) {
            console.warn('No candidate profile yet:', error);
            // Don't fail auth init if no candidate exists
          }
        }

        setLoadingAuth(false);
        setIsLoading(false);
      } catch (error) {
        console.error('Error checking auth:', error);
        // Only redirect if we're on a page that requires auth
        const currentPath = window.location.pathname;
        if (currentPath.includes('/candidate/interviews/')) {
          router.push('/');
        } else {
          // For public pages, just set loading to false
          setLoadingAuth(false);
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
  }, [interviewTakenId, router]);

  // Recorder visibility setup
  useEffect(() => {
    if (conversationId && streamRef.current) {
      console.log('Setting recorder to visible');
      setShowRecorder(true);
    }
  }, [conversationId, streamRef]);

  const playAudio = async (text: string) => {
    console.log('Playing audio', text);
    const audioBuffer = await fetch('/api/openai/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text })
    });
    const audioBlob = await audioBuffer.blob();
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    audio.onended = () => {
      URL.revokeObjectURL(audioUrl);
      setCurrentState(STATE.PRESS_TO_RECORD);
    };
    audio.play();
  };

  const handleUserRecording = async (audioBlob: Blob) => {
    console.log('handleUserRecording');
    const formData = new FormData();
    formData.append('audio', audioBlob);

    // TODO FIX THIS
    const response = await fetch('/api/openai/stt', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to convert speech to text');
    }

    const { text } = await response.json();

    const secondsLeftInTopic =
      INTERVIEW_DURATION * conversation[currentTopicIdx].finishTimeFraction -
      timer;

    const { message, completed } = await generateFollowUpQuestion(
      conversation[currentTopicIdx].messages[
        conversation[currentTopicIdx].messages.length - 1
      ].message,
      text,
      secondsLeftInTopic
    );

    const messages = [
      {
        message: text,
        type: 'user'
      },
      {
        message: message,
        type: 'agent'
      }
    ];

    setConversation(prev => {
      return prev.map((topic, idx) => {
        if (idx === currentTopicIdx) {
          return {
            ...topic,
            messages: [...topic.messages, ...messages]
          };
        }
        return topic;
      });
    });

    if (completed) {
      if (currentTopicIdx === conversation.length - 1) {
        setCurrentState(STATE.END);
        return;
      }

      setCurrentTopicIdx(currentTopicIdx + 1);
      // update topic completed to true
      setConversation(prev => {
        return prev.map((topic, idx) => {
          if (idx === currentTopicIdx) {
            return { ...topic, completed: true };
          }
          return topic;
        });
      });
    }

    setCurrentState(STATE.AGENT_ASKING);
  };

  // State transition effect
  useEffect(() => {
    if (
      currentState === STATE.AGENT_ASKING &&
      prevStateRef.current !== STATE.AGENT_ASKING
    ) {
      const currentTopic = conversation[currentTopicIdx];

      const currentMessage =
        currentTopic.messages[currentTopic.messages.length - 1];

      playAudio(currentMessage.message || currentMessage.text);
    }
  }, [currentState]);

  // Poll for interview status updates only when in 'initial' status
  useEffect(() => {
    const runGetInterviewStatus = async () => {
      const status = await getInterviewStatus(interviewTakenId as string);

      if (status === 'transitioning') {
        setInterviewStatus('transitioning');
      }

      // If status is "coding", move to coding page
      if (status === 'coding') {
        setInterviewStatus('coding');
      }

      // If status is "wrap_up", move to wrap-up page
      if (status === 'wrap_up') {
        // Only clear agent message if we're transitioning TO wrap_up status
        if (interviewStatus !== 'wrap_up') {
          setCurrentAgentMessage(null);
        }
        setInterviewStatus('wrap_up');
      }

      // If status is "finished", move to finished page
      if (status === 'finished') {
        setInterviewStatus('finished');
      }
    };

    if (!interviewTakenId) return;

    // Only poll if we're in 'initial' status (during technical interview)
    // This is when we're waiting for the webhook to trigger status change to 'coding'
    if (interviewStatus === 'transitioning' || interviewStatus === 'initial') {
      const interval = setInterval(runGetInterviewStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [interviewTakenId, interviewStatus]);

  // Timer effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (interviewStarted) {
      intervalId = setInterval(() => {
        setTimer(prevTimer => {
          const newTimer = prevTimer + 1;

          // Get current topic's time limit
          const currentTopic = conversation[currentTopicIdx];
          if (!currentTopic) return newTimer;

          const timeLimit =
            INTERVIEW_DURATION * currentTopic.finishTimeFraction;
          const timeLeft = timeLimit - newTimer;

          // Only show warnings when user is speaking
          if (currentState === STATE.USER_SPEAKING) {
            // Check for warning states
            if (timeLeft <= 0) {
              if (timeWarningState !== 'done') {
                setTimeWarningState('done');
                toast.error("Time's up! Wrapping up your answer...", {
                  position: 'top-center',
                  duration: 3000
                });

                // Only trigger recording cutoff once
                if (!recordingCutoffTriggeredRef.current) {
                  recordingCutoffTriggeredRef.current = true;
                  handleUserRecording(new Blob());
                }
              }
            } else if (timeLeft <= 10 && timeWarningState !== 'almost_done') {
              setTimeWarningState('almost_done');
              toast.warning(
                'Time is almost up! Consider wrapping up your answer.',
                {
                  position: 'top-center',
                  duration: 3000
                }
              );
            }
          }

          return newTimer;
        });
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    interviewStarted,
    currentTopicIdx,
    conversation,
    timeWarningState,
    currentState
  ]);

  // Reset warning state when moving to a new topic
  useEffect(() => {
    setTimeWarningState('none');
    recordingCutoffTriggeredRef.current = false;
  }, [currentTopicIdx]);

  // Reset timer when interview is reset
  useEffect(() => {
    if (!interviewStarted) {
      setTimer(0);
    }
  }, [interviewStarted]);

  // Business Logic

  const resetInterview = useCallback(() => {
    setCurrentState(STATE.START_INTERVIEW);
    setTimer(0);
    setConversation([]);
    setCurrentTopicIdx(0);
    setAnsweredQuestions([]);
    setIsFollowUp(false);
    setCurrentFollowUpIdx(0);
    setPendingNextStep(false);
    setInterviewStarted(false);
    setConsentGiven(false);
  }, []);

  const handleDeviceSetupComplete = useCallback(() => {
    localStorage.setItem(`webcam-setup-${interviewTakenId}`, 'true');
    setDeviceSetupShown(true);
    setInterviewStatus('initial');
  }, [interviewTakenId]);

  const handleInterviewStart = useCallback(() => {
    if (interviewStatus === 'wrap_up') {
      localStorage.setItem(`wrap-up-started-${interviewTakenId}`, 'true');
    } else {
      localStorage.setItem(`interview-started-${interviewTakenId}`, 'true');
    }

    setInterviewStarted(true);

    if (interviewStatus !== 'wrap_up') {
      setConsentVisible(false);
    }
  }, [interviewStatus, interviewTakenId]);

  // Initialize media stream when needed
  const initializeMediaStream = useCallback(async () => {
    try {
      return await initializeStream();
    } catch (error) {
      console.error('Failed to initialize media stream:', error);
      throw error;
    }
  }, [initializeStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
      // Reset states on unmount
      setDeviceSetupShown(false);
      setInterviewStarted(false);
      setConsentVisible(true);
      setConsentGiven(false);
      // Clear localStorage on unmount
      if (interviewTakenId) {
        localStorage.removeItem(`webcam-setup-${interviewTakenId}`);
        localStorage.removeItem(`interview-started-${interviewTakenId}`);
        localStorage.removeItem(`wrap-up-started-${interviewTakenId}`);
      }
    };
  }, [stopStream, interviewTakenId]);

  // Handle route changes
  useEffect(() => {
    const handleRouteChange = () => {
      stopStream();
      // Clear interview state when navigating away
      if (interviewTakenId) {
        localStorage.removeItem(`webcam-setup-${interviewTakenId}`);
        localStorage.removeItem(`interview-started-${interviewTakenId}`);
        localStorage.removeItem(`wrap-up-started-${interviewTakenId}`);
      }
    };

    window.addEventListener('beforeunload', handleRouteChange);
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('beforeunload', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [stopStream, interviewTakenId]);

  const value = {
    // State
    currentState,
    setCurrentState,
    timer,
    conversation,
    setConversation,
    currentTopicIdx,
    setCurrentTopicIdx,
    answeredQuestions,
    setAnsweredQuestions,
    isFollowUp,
    currentFollowUpIdx,
    pendingNextStep,
    interviewStarted,
    isLoading,
    consentGiven,
    deviceSetupShown,
    consentVisible,
    streamRef,
    screeningInfo,
    h1AnimationComplete,
    emojiAnimationComplete,
    showConsentModal,
    loadingAuth,
    sessionData,
    conversationId,
    candidateId,
    shouldStartRecording,
    showRecorder,
    timeWarningState,
    setTimeWarningState,
    mediaStream: stream,
    currentInterview,
    permissionsRevoked,

    // Actions
    setConsentGiven,
    resetInterview,
    interviewStatus,
    setInterviewStatus,
    setDeviceSetupShown,
    setConsentVisible,
    handleDeviceSetupComplete,
    handleInterviewStart,
    setH1AnimationComplete,
    setEmojiAnimationComplete,
    setShowConsentModal,
    setConversationId,
    setShouldStartRecording,
    setShowRecorder,
    handleUserRecording,
    initializeMediaStream,
    stopMediaStream: stopStream,
    setInterviewStarted,
    currentAgentMessage,
    setCurrentAgentMessage,
    setCurrentInterview,

    // Refs
    videoRecorderRef,
    audioRef
  };

  return (
    <InterviewContext.Provider value={value}>
      {children}
    </InterviewContext.Provider>
  );
}

export function useInterview() {
  const context = useContext(InterviewContext);
  if (context === undefined) {
    throw new Error('useInterview must be used within an InterviewProvider');
  }
  return context;
}
